import { doc, getDoc, setDoc, deleteField } from 'firebase/firestore';
import { CATALOG_NS, getCachedCatalog } from '../services/catalogCache';

const LOCAL_COMPARISON_KEY = 'comparisonProperties';

export function readLocalComparisonItems() {
  try {
    const raw = localStorage.getItem(LOCAL_COMPARISON_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getComparisonPropertyIds(items) {
  return mergeComparisonIds(getComparisonPropertyIdsRaw(items));
}

function getComparisonPropertyIdsRaw(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => (typeof item === 'string' ? item : item?.id))
    .filter(Boolean)
    .map(String);
}

export function getStoredComparisonIds(userData) {
  if (!userData) return [];

  const storedIds = Array.isArray(userData.comparisonPropertyIds)
    ? userData.comparisonPropertyIds.map(String).filter(Boolean)
    : [];

  if (storedIds.length > 0) return mergeComparisonIds(storedIds);

  if (Array.isArray(userData.comparisonProperties)) {
    return getComparisonPropertyIds(userData.comparisonProperties);
  }

  return [];
}

/** Resolve docs por id: cache de catálogo primeiro, depois getDoc em paralelo só do que falta. */
async function resolveDocsByIds(db, collectionName, catalogNs, catalogScope, ids) {
  const orderedIds = ids.map(String).filter((id, index, arr) => arr.indexOf(id) === index);
  if (!orderedIds.length) return [];

  const byId = new Map();
  const cached = getCachedCatalog(catalogNs, catalogScope);
  if (cached?.data?.length) {
    cached.data.forEach((item) => {
      if (item?.id != null) byId.set(String(item.id), item);
    });
  }

  const missing = orderedIds.filter((id) => !byId.has(id));
  if (missing.length) {
    const snaps = await Promise.allSettled(
      missing.map((id) => getDoc(doc(db, collectionName, id)))
    );
    snaps.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const snap = result.value;
      if (snap.exists()) {
        byId.set(missing[index], { id: snap.id, ...snap.data() });
      }
    });
  }

  return orderedIds
    .map((id) => byId.get(id))
    .filter(Boolean)
    .filter((item) => item.isInactive !== true);
}

export async function fetchPropertiesByIds(db, ids) {
  return resolveDocsByIds(db, 'properties', CATALOG_NS.PROPERTIES, 'public:all', ids);
}

export async function fetchCondominiumsByIds(db, ids) {
  return resolveDocsByIds(db, 'condominiums', CATALOG_NS.CONDOMINIUMS, 'public:all', ids);
}

function mergeComparisonIds(...lists) {
  const merged = [];
  for (const list of lists) {
    for (const id of list) {
      const normalized = String(id);
      if (!merged.includes(normalized)) merged.push(normalized);
    }
  }
  return merged;
}

export function dedupeComparisonProperties(properties) {
  const seen = new Set();
  return properties.filter((property) => {
    const id = String(typeof property === 'string' ? property : property?.id);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export async function saveComparisonPropertyIds(db, userId, properties) {
  const comparisonPropertyIds = getComparisonPropertyIds(properties);
  const payload = {
    comparisonPropertyIds,
    comparisonProperties: deleteField(),
    updatedAt: new Date(),
  };

  try {
    await setDoc(doc(db, 'users', userId), payload, { merge: true });
  } catch (error) {
    console.warn('Salvando comparação em userPreferences (fallback):', error);
    await setDoc(doc(db, 'userPreferences', userId), payload, { merge: true });
    try {
      await setDoc(
        doc(db, 'users', userId),
        { comparisonProperties: deleteField() },
        { merge: true }
      );
    } catch (cleanupError) {
      console.warn('Não foi possível remover comparação legada do users doc:', cleanupError);
    }
  }

  return comparisonPropertyIds;
}

export async function getRemoteComparisonIds(db, userId) {
  let userData = null;
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    userData = userSnap.exists() ? userSnap.data() : null;
  } catch (error) {
    console.warn('Não foi possível ler comparação em users:', error);
  }

  const fromUsers = getStoredComparisonIds(userData);
  if (fromUsers.length > 0) return fromUsers;

  // Fallback legado — silencioso se a coleção ainda não tiver rules
  try {
    const prefsSnap = await getDoc(doc(db, 'userPreferences', userId));
    if (prefsSnap.exists()) {
      return getStoredComparisonIds(prefsSnap.data());
    }
  } catch {
    // ignorar: userPreferences é opcional
  }

  return [];
}

export async function migrateLegacyComparisonField(db, userId, userData) {
  const legacyIds = getComparisonPropertyIds(userData?.comparisonProperties);
  if (!legacyIds.length) return false;

  const currentIds = getStoredComparisonIds(userData);
  const mergedIds = mergeComparisonIds(currentIds, legacyIds);
  await saveComparisonPropertyIds(
    db,
    userId,
    mergedIds.map((id) => ({ id }))
  );
  return true;
}

export async function loadUserComparison(db, userId) {
  const localItems = readLocalComparisonItems();
  const localIds = getComparisonPropertyIds(localItems);

  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    const userData = userSnap.exists() ? userSnap.data() : null;

    if (userData) {
      await migrateLegacyComparisonField(db, userId, userData);
    }
  } catch (error) {
    console.warn('Não foi possível migrar comparação legada:', error);
  }

  const remoteIds = await getRemoteComparisonIds(db, userId);
  const mergedIds = mergeComparisonIds(remoteIds, localIds);

  if (localIds.length > 0) {
    try {
      await saveComparisonPropertyIds(
        db,
        userId,
        mergedIds.map((id) => ({ id }))
      );
      localStorage.removeItem(LOCAL_COMPARISON_KEY);
    } catch (error) {
      console.warn('Não foi possível sincronizar comparação local:', error);
    }
  }

  let properties = mergedIds.length ? await fetchPropertiesByIds(db, mergedIds) : [];

  if (properties.length < mergedIds.length && localItems.length > 0) {
    const localById = new Map(
      localItems.map((item) => {
        const id = String(typeof item === 'string' ? item : item.id);
        return [id, typeof item === 'string' ? { id } : item];
      })
    );

    properties = mergedIds
      .map((id) => properties.find((property) => String(property.id) === id) || localById.get(id))
      .filter(Boolean);
  }

  return dedupeComparisonProperties(properties);
}

export function hasLocalCacheToMigrate() {
  return Boolean(
    localStorage.getItem('localFavorites') ||
      localStorage.getItem('localCondominiumFavorites') ||
      localStorage.getItem(LOCAL_COMPARISON_KEY) ||
      localStorage.getItem('chatMessages')
  );
}
